import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import OrgWizardStepper from '../../components/OrgWizardStepper';
import { SaveIcon, CheckCircleIcon } from '../../assets/icons';
import Breadcrumbs from '../../components/Breadcrumbs';
import OrganizationDetailsStep from './createOrg/OrganizationDetailsStep';
import DefineBoundaryStep, {
  type HierarchyView,
  type AcOption,
} from './createOrg/DefineBoundaryStep';
import BuildHierarchyStep from './createOrg/BuildHierarchyStep';
import DataImportStep from './createOrg/DataImportStep';
import { initialOrgValues, orgValidationSchema, type OrgFormValues } from './createOrg/types';

interface StepMeta {
  label: string;
  title: string;
  subtitle: string;
}

const STEPS_META: StepMeta[] = [
  {
    label: 'Create New',
    title: 'Create Organization',
    subtitle: 'Register a new entity within the organizational hierarchy.',
  },
  {
    label: 'Define Boundary',
    title: 'Define Operational Boundary',
    subtitle: 'Choose the administrative node to define election commission jurisdiction.',
  },
  {
    label: 'Build Hierarchy',
    title: 'Organization Hierarchy Configuration',
    subtitle: 'Configure and sequence the administrative hierarchy of your organization.',
  },
  {
    label: 'Structure Explorer',
    title: 'Organization Hierarchy Data Import',
    subtitle: 'Manage and visualize the political structure across all levels.',
  },
];
// Note: the 5th step ("Create Organization User") is intentionally hidden for now;
// completing step 4 returns to the Organizations list.

const LAST_STEP = STEPS_META.length - 1;

/** Single-page 5-step wizard. Steps stay mounted so values persist when you go back to edit. */
export default function CreateOrganizationWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  // Live selection from step 2, and the snapshot confirmed for display in step 3.
  const [liveSelection, setLiveSelection] = useState<HierarchyView[]>([]);
  const [confirmedSelection, setConfirmedSelection] = useState<HierarchyView[]>([]);
  // Ordered organization-level names from step 3 (used to prefill step 4's template).
  const [hierarchyLevels, setHierarchyLevels] = useState<string[]>([]);
  // Per-constituency settlement branch chosen in step 2 — filters step 4's Level 2.
  const [liveBranches, setLiveBranches] = useState<Record<string, 'urban' | 'rural'>>({});
  const [selectedBranches, setSelectedBranches] = useState<Record<string, 'urban' | 'rural'>>({});
  // Constituencies chosen in step 2 — become step 4's Level 1 list.
  const [liveConstituencies, setLiveConstituencies] = useState<AcOption[]>([]);
  const [selectedConstituencies, setSelectedConstituencies] = useState<AcOption[]>([]);

  const finish = (values: OrgFormValues) => {
    // TODO: wire to a createOrganization mutation once the API exists.
    // eslint-disable-next-line no-console
    console.log('Create organization', values);
    navigate('/admin/organizations');
  };

  const formik = useFormik<OrgFormValues>({
    initialValues: initialOrgValues,
    validationSchema: orgValidationSchema,
    onSubmit: finish,
  });

  const meta = STEPS_META[step];
  const isLast = step === LAST_STEP;

  const goPrimary = () => {
    // Confirm Selection (step 2 → 3): snapshot what was selected so step 3 can show it.
    if (step === 1) {
      setConfirmedSelection(liveSelection);
      setSelectedBranches(liveBranches);
      setSelectedConstituencies(liveConstituencies);
    }
    if (isLast) finish(formik.values);
    else setStep((s) => Math.min(LAST_STEP, s + 1));
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb + heading */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[{ label: 'Organizations', to: '/admin/organizations' }, { label: meta.label }]}
        />
        <h1 className="pt-1 text-2xl font-semibold tracking-[-0.24px] text-ink-900">{meta.title}</h1>
        <p className="text-sm text-ink-secondary">{meta.subtitle}</p>
      </div>

      {/* Clickable stepper — jump to any step to review/edit (5th step hidden for now) */}
      <OrgWizardStepper activeStep={step} onStepClick={setStep} visibleSteps={STEPS_META.length} />

      {/* Step panels — kept mounted so entered values survive navigation */}
      <div className={step === 0 ? '' : 'hidden'}>
        <OrganizationDetailsStep formik={formik} />
      </div>
      <div className={step === 1 ? '' : 'hidden'}>
        <DefineBoundaryStep
          onSelectionChange={setLiveSelection}
          onBranchesChange={setLiveBranches}
          onConstituenciesChange={setLiveConstituencies}
        />
      </div>
      <div className={step === 2 ? '' : 'hidden'}>
        <BuildHierarchyStep selection={confirmedSelection} onLevelsChange={setHierarchyLevels} />
      </div>
      <div className={step === 3 ? '' : 'hidden'}>
        <DataImportStep
          onConfirm={() => finish(formik.values)}
          hierarchyLevels={hierarchyLevels}
          branches={selectedBranches}
          constituencies={selectedConstituencies}
        />
      </div>

      {/* Wizard navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="flex h-10 items-center justify-center rounded-lg border border-ink-secondary px-[21px] text-xs font-medium tracking-[0.6px] text-ink-secondary transition-colors hover:bg-brand-shade disabled:invisible"
        >
          Back
        </button>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/organizations')}
            className="flex h-10 items-center justify-center rounded-lg border border-ink-secondary px-[21px] text-xs font-medium tracking-[0.6px] text-ink-secondary transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          {/* The last step (Data Import) provides its own "Confirm Selection" action. */}
          {!isLast && (
            <button
              type="button"
              onClick={goPrimary}
              className="flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-5 text-xs font-medium tracking-[0.6px] text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              {step === 0 && <SaveIcon className="size-[13.5px]" />}
              {step === 0 ? 'Save Organization' : step === 1 ? 'Confirm Selection' : 'Save'}
              {step === 1 && <CheckCircleIcon className="size-4" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
